/**
 * License plate utilities — single source of truth. The legacy code has 4+
 * scattered implementations of normalize/format that disagree on edge cases:
 *   - some strip non-digits with `replace(/\D/g, '')`
 *   - some slice to 8 chars, others to 7
 *   - some return empty string, others return `null`
 *
 * Israeli license plates have 7 or 8 digits. Formatting:
 *   7-digit: `XX-XXX-XX`   (e.g. 1234567 → 12-345-67)
 *   8-digit: `XXX-XX-XXX`  (e.g. 12345678 → 123-45-678)
 */

const MIN_LEN = 7;
const MAX_LEN = 8;

/**
 * Strip non-digits and clamp to 8 characters. Returns the canonical plate string.
 * @param {unknown} input
 * @returns {string} digits only, possibly empty
 */
export function normalize(input) {
  return String(input ?? '').replace(/\D/g, '').slice(0, MAX_LEN);
}

/**
 * @param {unknown} input
 * @returns {boolean} true if normalize(input) is 7 or 8 digits
 */
export function isValid(input) {
  const n = normalize(input);
  return n.length >= MIN_LEN && n.length <= MAX_LEN;
}

/**
 * Pretty-format for display. Returns the input digits unchanged if length is
 * neither 7 nor 8 (so the caller can show partial input as the user types).
 * @param {unknown} input
 * @returns {string}
 */
export function format(input) {
  const d = normalize(input);
  if (d.length === 7) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
  if (d.length === 8) return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
  return d;
}

/**
 * Render the plate as an inline HTML badge (LTR forced for RTL pages).
 * Returns "-" for empty/invalid input so callers don't render an empty badge.
 * @param {unknown} input
 * @param {{ small?: boolean }} [options]
 * @returns {string}
 */
export function badgeHtml(input, options = {}) {
  const d = normalize(input);
  if (!d) return '-';
  const klass = options.small ? 'plate-badge small' : 'plate-badge';
  return `<span class="${klass}" dir="ltr">${format(d)}</span>`;
}
